import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Navbar from "./components/site/Navbar";
import Footer from "./components/site/Footer";
import MobileBottomBar from "./components/site/MobileBottomBar";
import SplashIntro from "./components/site/SplashIntro";

import Home from "./pages/Home";
import { ServicesList, ServiceDetail } from "./pages/Services";
import { ConditionsList, ConditionDetail } from "./pages/Conditions";
import { About, Team, FirstVisit, Faqs, PatientStories } from "./pages/CorePages";
import { BlogList, BlogPost } from "./pages/Blog";
import { BookAppointment, Contact } from "./pages/BookAppointment";
import { AclPillar, NeuroPillar } from "./pages/Pillars";
import MaternalCare from "./pages/MaternalCare";
import AmitSharmaProfile from "./pages/AmitSharmaProfile";
import LocationPage from "./pages/LocationPage";
import { AdminLogin, AdminDashboard, AdminLeads, AdminBlog, AdminAnalytics, AdminReviews } from "./pages/admin/AdminPages";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    // GA4 page_view tracking (guarded — no-op unless __GA_ID set)
    if (typeof window !== "undefined" && window.gtag && window.__GA_ID) {
      window.gtag("event", "page_view", {
        page_path: pathname,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }, [pathname]);

  // Global tel: click listener → fires phone_clicked GA4 event
  useEffect(() => {
    const onClick = (e) => {
      const a = e.target.closest && e.target.closest('a[href^="tel:"]');
      if (!a || !window.gtag || !window.__GA_ID) return;
      window.gtag("event", "phone_clicked", {
        page_path: window.location.pathname,
        number: a.getAttribute("href").replace("tel:", ""),
      });
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  return null;
}

function RequireAuth({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="p-10 text-brand-ink2">Loading…</div>;
  if (!user || user === false) return <Navigate to="/admin/login" replace />;
  return children;
}

function SiteChrome({ children }) {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  return (
    <>
      {!isAdmin && <SplashIntro />}
      {!isAdmin && <Navbar />}
      <main data-testid="site-main">{children}</main>
      {!isAdmin && <Footer />}
      {!isAdmin && <MobileBottomBar />}
    </>
  );
}

export default function App() {
  return (
    <div className="App min-h-screen bg-brand-bg">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <SiteChrome>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/team" element={<Team />} />
              <Route path="/services" element={<ServicesList />} />
              <Route path="/services/acl-rehab" element={<AclPillar />} />
              <Route path="/services/neuro-rehab" element={<NeuroPillar />} />
              <Route path="/services/:slug" element={<ServiceDetail />} />
              <Route path="/conditions" element={<ConditionsList />} />
              <Route path="/conditions/acl" element={<AclPillar />} />
              <Route path="/conditions/neuro" element={<NeuroPillar />} />
              <Route path="/conditions/:slug" element={<ConditionDetail />} />
              <Route path="/blog" element={<BlogList />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/first-visit" element={<FirstVisit />} />
              <Route path="/faqs" element={<Faqs />} />
              <Route path="/patient-stories" element={<PatientStories />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/book" element={<BookAppointment />} />
              <Route path="/maternal-care" element={<MaternalCare />} />
              <Route path="/team/amit-sharma" element={<AmitSharmaProfile />} />
              <Route path="/areas/:area" element={<LocationPage />} />

              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<RequireAuth><AdminDashboard /></RequireAuth>} />
              <Route path="/admin/leads" element={<RequireAuth><AdminLeads /></RequireAuth>} />
              <Route path="/admin/blog" element={<RequireAuth><AdminBlog /></RequireAuth>} />
              <Route path="/admin/reviews" element={<RequireAuth><AdminReviews /></RequireAuth>} />
              <Route path="/admin/analytics" element={<RequireAuth><AdminAnalytics /></RequireAuth>} />

              <Route path="*" element={
                <div className="container-wide py-24 text-center">
                  <h1 className="font-heading text-4xl text-brand-ink">Page not found</h1>
                  <p className="text-brand-ink2 mt-3">This page may have moved. Please head back home.</p>
                  <a href="/" className="btn-primary mt-6 inline-flex">Go home</a>
                </div>
              } />
            </Routes>
          </SiteChrome>
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
